/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './ExpressionWatchListener';
import { expressionWatchListenerHelpRenders } from '../ExpressionWatcher/ExpressionWatcher_Help';

const ExpressionWatchListenerManager: ComponentManagerFn<
  'ExpressionWatchListener'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'ExpressionWatchListener'>(
      platform,
      'ExpressionWatchListener',
    ),
    type: 'ExpressionWatchListener',
    name: 'Expression Watch Listener',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnInitialize', 'TriggerOnChange'] as ItemType[]).includes(
        childType,
      ),
    defaultValue,
    props: {
      watcherItemId: {
        label: 'Watcher Item Id',
        type: 'ItemID',
        initialFilter: 'ExpressionWatcher',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'ExpressionWatcher') {
            return `Item id '${value}' doesn't belong to a Expression Watcher`;
          }
          return '';
        },
        required: true,
        helpText:
          'Choose the Item ID of the Expression Watcher component defined at the page level.',
      },
      listenerGroupKey: {
        label: 'Listener Group Key',
        type: 'string',
        helpText:
          'Provide a key that will be used to register for changes. If multiple, listeners share the same key, then only one of them will recieve the initialize/change event',
      },
    },
    getDisplayText: (item) => item.props.watcherItemId,
    category: 'Others',
    icon: { iconName: 'bolt' },
    noOverlay: true,
    description: `Expression Watch Listener used in association with \`Expression Watcher\`.
It supports two triggers. \`Trigger On Initialize\` is fired only once for the given \`Listener Group Key\` on first time rendering.
\`Trigger On Change\` will be fired whenever there is change in the expression's outcome.
When multiple Listeners are rendered with the given \`Listener Group Key\`, then only one of the listener's \`Trigger On Change\` is called.

For example, if you have a multiple charts across different tabs to be refreshed and if some of the charts share the same data store, then
you can use the data source alias as the group key and have multiple \`Expression Watch Listener\` on each of the charts to refresh them accordingly.
This way we will make sure that only the charts that are visible to the user are refreshed and if there are multiple charts with the same data store, then only one of them will be refreshed.
`,
    helpRenders: expressionWatchListenerHelpRenders,
  };
};

export default ExpressionWatchListenerManager;
